import React, { useEffect, useRef, useState } from 'react';
import { List, ListChecks, ListOrdered, Link as LinkIcon } from 'lucide-react';
import { useLanguage } from '../i18n';

// ─────────────────────────────────────────────────────────────────────────
// RichTextEditor — a TRUE WYSIWYG editor (Manual edition Description Board #106)
//
// Why this file exists (the PO rejection): the first attempt at #106 was a
// `<textarea>` + a toolbar that INSERTED RAW MARKDOWN (`**bold**`, `## Heading`,
// `- item`) — so the user saw literal `#`/`**`/`-` symbols while editing. The PO
// rejected it as "Markdown Leakage in WYSIWYG Editor": the editing surface must
// look like Word/Google Docs — styled text as-you-type, NO raw syntax visible,
// toolbar buttons apply VISUAL formatting, and the Edit view must be visually
// identical to the Saved view.
//
// The fix is a `contentEditable` surface driven by `document.execCommand` (the
// standard, battle-tested WYSIWYG primitives): Bold wraps in <b>, Bullet/Numbered
// build real <ul>/<ol>, Heading uses formatBlock → <h2>. The browser renders those
// as bold/bullets/big-headings natively, so NO markdown symbol is ever shown.
//
// STORAGE stays markdown (the ticket description + comments are markdown everywhere
// else — the view renders with react-markdown, BE tests + exports read markdown), so
// the editor round-trips markdown ↔ HTML for the supported subset:
//   headings (# .. ######), **bold**, *italic*, `-`/`*` bullets, `1.` numbered,
//   [label](href) links.
//
// WHICH buttons the toolbar offers is a per-instance `toolbar` prop (#281): the
// board surfaces keep the historical heading-first set, the note editor passes
// its mockup's set (bold · italic · bullets · numbered · checklist · link).
// `markdownToHtml` seeds the editor from stored markdown (so entering edit mode shows
// the SAME styling as the view — "State Consistency"); `htmlToMarkdown` serializes the
// edited DOM back to markdown on save/submit.
//
// CHECKLISTS (tick boxes note #314, re-open) are the one feature gated behind an
// OPT-IN `tasks` prop, and the gate is deliberate: `markdownToHtml` is shared, so
// rendering a GFM task item (`- [ ]`) as an interactive circle unconditionally
// would silently change the board description + comment editors too. With
// `tasks` off the old behaviour is byte-identical — the box stays literal text.
// With it on, a task item becomes
//   <li data-task="pending|done">
//     <span class="rte-task-toggle" data-task-toggle contenteditable="false"></span>
//     <span class="rte-task-text">…</span>
//   </li>
// so the circle is a real, clickable, hover-able element (a ::before pseudo-marker
// could not be hit-tested or asserted on) that the caret can never land inside,
// and the strike-through on a completed item hits only the TEXT, never the circle.
// Its visuals live in `src/index.css` under `.rte-task-toggle` — ONE rule set,
// shared verbatim with the note READ view, which is what makes the two surfaces
// look identical. STORAGE IS STILL MARKDOWN: the circle carries no text, is
// skipped by the serializer, and the li's `data-task` is what writes `[ ]`/`[x]`
// back out.
//
// The editor is UNCONTROLLED (React never re-renders its children): innerHTML is
// seeded once via a ref, and edits are pushed UP as markdown via onChange — this is
// what keeps the caret stable and stops "removing one character breaks the block"
// (the old bug came from re-parsing markdown on every keystroke).
// ─────────────────────────────────────────────────────────────────────────

// ── markdown → HTML (seed the editor) ──────────────────────────────────────

const escapeHtml = (s) => String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

// Bold/italic only — split out so a link's LABEL can be emphasized without
// re-running the (link-extracting) outer pass on it.
const emphasizeMd = (s0) => {
    let s = s0;
    // Bold first (**..** / __..__) so the italic pass doesn't chew the markers.
    s = s.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>');
    s = s.replace(/__([^_]+?)__/g, '<strong>$1</strong>');
    // Italic (*..* / _.._) — single markers not adjacent to another marker.
    s = s.replace(/(^|[^*])\*(?!\s)([^*\n]+?)\*(?!\*)/g, '$1<em>$2</em>');
    // `_` emphasis is INTRAWORD-SAFE (CommonMark's rule): the opening marker must
    // follow the start/whitespace/punctuation and the closing one must not be
    // followed by a word char. Without this, ordinary text like `snake_case_name`
    // — or a URL such as `example.com/a_b_c` — comes back italicized, silently
    // corrupting it on every markdown round-trip.
    s = s.replace(/(^|[^\w*_])_(?!\s)([^_\n]+?)_(?![\w*_])/g, '$1<em>$2</em>');
    return s;
};

// A URL is not markdown: `https://x.com/a_b_c` must NOT come back italicized. So
// links are lifted out into placeholders BEFORE the emphasis passes and restored
// after (their label is emphasized on its own).
const LINK_SLOT = '\uE000';

const inlineMdToHtml = (text) => {
    const links = [];
    let s = escapeHtml(text).split(LINK_SLOT).join('').replace(
        /\[([^\]\n]*)\]\(([^)\s]+)\)/g,
        (_m, label, href) => {
            links.push({ label, href });
            return `${LINK_SLOT}L${links.length - 1}${LINK_SLOT}`;
        },
    );
    s = emphasizeMd(s);
    if (links.length) {
        s = s.replace(/\uE000L(\d+)\uE000/g, (_m, i) => {
            const { label, href } = links[Number(i)] || {};
            if (href == null) return '';
            return `<a href="${href}">${emphasizeMd(label) || href}</a>`;
        });
    }
    return s;
};

// ── Checklist items (tick boxes note #314) ─────────────────────────────────
//
// The GFM box, matched on what is LEFT of a list marker (the marker itself is
// already stripped by the caller). It must be followed by whitespace or the end
// of the line, so `- [link](url)` and `- [ ]x` are never mistaken for one — the
// same rule `utils/markdownTasks.js` applies to the source.
const TASK_BOX_RE = /^\[([ xX])\](\s+|$)/;
const TASK_ATTR = 'data-task';
const TASK_TEXT_SEL = '.rte-task-text';

// One builder for BOTH paths (seeding via innerHTML, and creating an item live
// on Enter / the toolbar) so the markup can never drift between them.
const taskToggleHtml = (done) => `<span class="rte-task-toggle" data-task-toggle="" data-testid="rte-task-toggle" data-task-state="${done ? 'done' : 'pending'}" contenteditable="false" role="checkbox" aria-checked="${done ? 'true' : 'false'}" aria-label="Toggle checklist item"></span>`;

// An EMPTY item still needs something the caret can sit in, hence the `<br>`:
// it serializes to a newline that `listMd` collapses away, so it never reaches
// storage.
const taskLiHtml = (done, innerHtml) => `<li ${TASK_ATTR}="${done ? 'done' : 'pending'}">${taskToggleHtml(done)}<span class="rte-task-text">${innerHtml || '<br>'}</span></li>`;

const elementFromHtml = (html) => {
    const tpl = document.createElement('template');
    tpl.innerHTML = html;
    return tpl.content.firstElementChild;
};

// `rest` is one list item with its bullet/number marker already removed.
const listItemHtml = (rest, tasks) => {
    const m = tasks ? TASK_BOX_RE.exec(rest) : null;
    if (!m) return `<li>${inlineMdToHtml(rest)}</li>`;
    return taskLiHtml(m[1] !== ' ', inlineMdToHtml(rest.slice(m[0].length)));
};

export const markdownToHtml = (md, { tasks = false } = {}) => {
    const src = String(md || '').replace(/\r\n?/g, '\n');
    if (!src.trim()) return '';
    const lines = src.split('\n');
    const out = [];
    let i = 0;
    while (i < lines.length) {
        const line = lines[i];
        const h = /^(#{1,6})\s+(.*)$/.exec(line);
        if (h) {
            const level = h[1].length;
            out.push(`<h${level}>${inlineMdToHtml(h[2])}</h${level}>`);
            i += 1;
            continue;
        }
        if (/^\s*[-*]\s+/.test(line)) {
            const items = [];
            while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
                items.push(listItemHtml(lines[i].replace(/^\s*[-*]\s+/, ''), tasks));
                i += 1;
            }
            out.push(`<ul>${items.join('')}</ul>`);
            continue;
        }
        if (/^\s*\d+\.\s+/.test(line)) {
            const items = [];
            while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
                items.push(listItemHtml(lines[i].replace(/^\s*\d+\.\s+/, ''), tasks));
                i += 1;
            }
            out.push(`<ol>${items.join('')}</ol>`);
            continue;
        }
        if (!line.trim()) { i += 1; continue; }
        // Paragraph: gather consecutive plain lines, joined by <br>.
        const para = [];
        while (
            i < lines.length && lines[i].trim()
            && !/^(#{1,6})\s+/.test(lines[i])
            && !/^\s*[-*]\s+/.test(lines[i])
            && !/^\s*\d+\.\s+/.test(lines[i])
        ) {
            para.push(inlineMdToHtml(lines[i]));
            i += 1;
        }
        out.push(`<p>${para.join('<br>')}</p>`);
    }
    return out.join('');
};

// ── HTML → markdown (serialize on save/submit) ─────────────────────────────

const styleHasBold = (el) => {
    const s = (el.getAttribute && el.getAttribute('style')) || '';
    return /font-weight\s*:\s*(bold|bolder|[6-9]00)/i.test(s);
};
const styleHasItalic = (el) => {
    const s = (el.getAttribute && el.getAttribute('style')) || '';
    return /font-style\s*:\s*italic/i.test(s);
};

// Wrap inner with a marker while keeping any surrounding whitespace OUTSIDE the
// markers (`** bold **` is not valid markdown; `**bold**` is).
const wrapInline = (inner, marker) => {
    const t = inner.trim();
    if (!t) return inner;
    const lead = (inner.match(/^\s*/) || [''])[0];
    const trail = (inner.match(/\s*$/) || [''])[0];
    return `${lead}${marker}${t}${marker}${trail}`;
};

// Serialize ONE node (text or element) including its OWN inline formatting — so a
// root-level `<b>bold</b>` (Chromium can leave inline elements directly under the
// editor root) keeps its `**` markers instead of being flattened to plain text.
const serializeInlineNode = (c) => {
    if (c.nodeType === 3) return c.textContent;                   // text node
    if (c.nodeType !== 1) return '';                              // comment, etc.
    // A checklist circle is CHROME, not content (#314): it carries no text of
    // its own, and its state travels on the li's `data-task` instead. Skipping
    // it here is what keeps storage clean markdown no matter how the browser
    // has nested things around it.
    if (c.hasAttribute && c.hasAttribute('data-task-toggle')) return '';
    const tag = c.tagName.toLowerCase();
    if (tag === 'br') return '\n';
    const inner = inlineHtmlToMd(c);
    if (inner === '') return '';
    // A link survives the round-trip as `[label](href)` — without this the
    // toolbar's Link button would produce an <a> that silently degrades to plain
    // text on the very next save.
    if (tag === 'a') {
        const href = (c.getAttribute && c.getAttribute('href')) || '';
        return href ? `[${inner.trim()}](${href})` : inner;
    }
    const bold = tag === 'b' || tag === 'strong' || styleHasBold(c);
    const italic = tag === 'i' || tag === 'em' || styleHasItalic(c);
    if (bold) return wrapInline(inner, '**');
    if (italic) return wrapInline(inner, '*');
    return inner;
};

function inlineHtmlToMd(node) {
    let out = '';
    node.childNodes.forEach((c) => { out += serializeInlineNode(c); });
    return out;
}

const headingMd = (el) => {
    const level = Number(el.tagName[1]);
    const text = inlineHtmlToMd(el).replace(/\s*\n\s*/g, ' ').trim();
    return text ? `${'#'.repeat(level)} ${text}` : '';
};

const listMd = (el) => {
    const ordered = el.tagName.toLowerCase() === 'ol';
    const items = Array.from(el.children).filter(
        (li) => li.tagName && li.tagName.toLowerCase() === 'li',
    );
    return items
        .map((li) => ({ li, text: inlineHtmlToMd(li).replace(/\s*\n\s*/g, ' ').trim() }))
        // An item with no text is dropped — the historical contract for plain
        // bullets, which a checklist item deliberately inherits: the empty item
        // left behind by a final Enter is scaffolding, not content.
        .filter(({ text }) => text !== '')
        .map(({ li, text }, idx) => {
            const state = li.getAttribute ? li.getAttribute(TASK_ATTR) : null;
            const box = state ? (state === 'done' ? '[x] ' : '[ ] ') : '';
            return `${ordered ? `${idx + 1}. ` : '- '}${box}${text}`;
        })
        .join('\n');
};

// Does this element contain BLOCK-level children? contentEditable/execCommand can nest a
// <ul>/<h2> inside a <p>/<div> (e.g. Chromium's insertUnorderedList yields
// `<p><ul><li>…</li></ul></p>`), so we must recurse into those wrappers rather than
// flattening them (which would drop the list/heading markers — a real serialization bug).
const isBlockTag = (t) => t === 'ul' || t === 'ol' || t === 'p' || t === 'div'
    || t === 'blockquote' || /^h[1-6]$/.test(t);
const hasBlockChild = (el) => Array.from(el.children).some(
    (c) => c.tagName && isBlockTag(c.tagName.toLowerCase()),
);

// Manually typed lists (Manual Notes #411) — the shape a manually-typed bullet,
// numbered item or checkbox (`- item`, `1. item`, `- [ ] item`) takes as its OWN
// serialized block, before it has ever been through a real <ul>/<ol>. A plain
// contentEditable line has no execCommand structure at all: pressing Enter after
// typing "- Buy milk" makes the browser wrap the NEXT line in its own sibling
// <div> (Chromium) with nothing marking it as "part of a list" — each manually
// typed line is, to the DOM, an ordinary standalone paragraph. `\S` after the
// marker keeps a bare "-" or "1." (no text yet) from being mistaken for one.
const MANUAL_BULLET_RE = /^\s*[-*]\s+\S/;
const MANUAL_ORDERED_RE = /^\s*\d+\.\s+\S/;
const manualListKind = (md) => {
    if (MANUAL_BULLET_RE.test(md)) return 'bullet';
    if (MANUAL_ORDERED_RE.test(md)) return 'ordered';
    return null;
};

export const htmlToMarkdown = (root) => {
    if (!root) return '';
    const parts = [];
    const push = (md) => { if (md && md.trim()) parts.push(md.trim()); };
    const walk = (parent) => {
        // Consecutive INLINE nodes (bare text, <b>, <i>, spans, <br>) are coalesced
        // into one paragraph run — contentEditable often leaves them directly under
        // the root, and "hello <b>bold</b> world" must stay ONE line.
        let run = '';
        const flushRun = () => { if (run.trim()) push(run.trim()); run = ''; };
        parent.childNodes.forEach((node) => {
            const isElem = node.nodeType === 1;
            const tag = isElem ? node.tagName.toLowerCase() : '';
            if (isElem && /^h[1-6]$/.test(tag)) { flushRun(); push(headingMd(node)); return; }
            if (isElem && (tag === 'ul' || tag === 'ol')) { flushRun(); push(listMd(node)); return; }
            // Wrapper holding block children (e.g. Chromium's `<p><ul>…</ul></p>`): recurse.
            if (isElem && hasBlockChild(node)) { flushRun(); walk(node); return; }
            if (isElem && (tag === 'p' || tag === 'div' || tag === 'blockquote')) {
                flushRun();
                push(inlineHtmlToMd(node).trim());
                return;
            }
            // Inline node (text, b/strong, i/em, span, br, …) — accumulate into the run.
            run += serializeInlineNode(node);
        });
        flushRun();
    };
    walk(root);
    // Join top-level blocks with a blank line, EXCEPT between two adjacent
    // manually-typed list lines of the SAME kind (#411) — those join with a
    // single newline instead, exactly like a real <ul>/<ol> already serializes
    // (see listMd above). Without this, `markdownToHtml`'s list scanner sees a
    // blank line between every hand-typed "- item" and starts a brand-new
    // one-item list at each one: a bullet/numbered/checklist list a user typed
    // by hand "disappeared" into N separate single-item lists on the very next
    // reload — nothing was deleted, but it no longer read as the list it was.
    // Known, accepted trade-off: two GENUINELY separate same-kind lists with
    // NOTHING at all between them (not even a blank line) would also merge on
    // the next round trip — an edge case the toolbar path rarely produces
    // (adjacent same-type lists typically merge into one <ul> already), and far
    // rarer than the bug this exists to fix.
    let out = '';
    let prevKind = null;
    parts.forEach((part, idx) => {
        const kind = manualListKind(part);
        if (idx === 0) {
            out = part;
        } else if (kind && kind === prevKind) {
            out += `\n${part}`;
        } else {
            out += `\n\n${part}`;
        }
        prevKind = kind;
    });
    return out.replace(/\n{3,}/g, '\n\n').trim();
};

// ── Live checklist DOM helpers (#314) ──────────────────────────────────────
//
// These mutate the editing surface directly rather than going through
// execCommand: there is no browser command for "task item", and hand-built DOM
// is the only way to guarantee the new item carries its circle (Chromium's own
// Enter handling does not reliably clone a contenteditable=false child).

const taskTextOf = (li) => li.querySelector(TASK_TEXT_SEL) || li;

const setTaskState = (li, done) => {
    li.setAttribute(TASK_ATTR, done ? 'done' : 'pending');
    const toggle = li.querySelector('[data-task-toggle]');
    if (toggle) {
        toggle.setAttribute('data-task-state', done ? 'done' : 'pending');
        toggle.setAttribute('aria-checked', done ? 'true' : 'false');
    }
};

const makeTaskLi = (done = false) => elementFromHtml(taskLiHtml(done, ''));

// Turn an ordinary <li> into a checklist item, keeping its content intact.
const makeTask = (li) => {
    if (li.hasAttribute(TASK_ATTR)) return;
    const text = document.createElement('span');
    text.className = 'rte-task-text';
    while (li.firstChild) text.appendChild(li.firstChild);
    if (!text.firstChild) text.appendChild(document.createElement('br'));
    li.appendChild(elementFromHtml(taskToggleHtml(false)));
    li.appendChild(text);
    li.setAttribute(TASK_ATTR, 'pending');
};

// …and back to an ordinary bullet, unwrapping the text span.
const unmakeTask = (li) => {
    li.removeAttribute(TASK_ATTR);
    const toggle = li.querySelector('[data-task-toggle]');
    if (toggle) toggle.remove();
    const text = li.querySelector(TASK_TEXT_SEL);
    if (text) {
        while (text.firstChild) li.insertBefore(text.firstChild, text);
        text.remove();
    }
};

// Backspacing at the head of an item deletes the circle (it is an atomic
// contenteditable=false node). Rather than fight the user by re-inserting it,
// the item degrades to a plain bullet — so the surface never shows a "task"
// with no control, and what is serialized always matches what is on screen.
const normalizeTasks = (root) => {
    root.querySelectorAll(`li[${TASK_ATTR}]`).forEach((li) => {
        if (!li.querySelector('[data-task-toggle]')) unmakeTask(li);
    });
};

const placeCaret = (node, atStart) => {
    const sel = window.getSelection();
    if (!sel || !node) return;
    const range = document.createRange();
    range.selectNodeContents(node);
    range.collapse(!!atStart);
    sel.removeAllRanges();
    sel.addRange(range);
};

const closestTaskLi = (root, node) => {
    let n = node && node.nodeType === 3 ? node.parentElement : node;
    while (n && n !== root) {
        if (n.tagName === 'LI' && n.hasAttribute(TASK_ATTR)) return n;
        n = n.parentElement;
    }
    return null;
};

// Every <li> the current selection touches. A nested list makes the ANCESTOR
// item intersect too, so the ones that merely CONTAIN another hit are dropped —
// the toolbar must convert what the user pointed at, not its whole subtree.
const selectedListItems = (root) => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return [];
    const range = sel.getRangeAt(0);
    const hits = Array.from(root.querySelectorAll('li')).filter((li) => {
        try { return range.intersectsNode(li); } catch { return false; }
    });
    return hits.filter((li) => !hits.some((other) => other !== li && li.contains(other)));
};

// ── Toolbar ────────────────────────────────────────────────────────────────
//
// Both of these are MODULE level on purpose. Defining a component inside a
// render body gives it a fresh type on every render, so React unmounts and
// remounts every button each time state changes — which drops the user's
// pointer target mid-click (and detaches element handles under test).

// Titles are resolved at render time (via titleKey/titleFallback) since this
// object is module-level and cannot call the t() hook.
const TOOLBAR_BUTTONS = {
    header: { titleKey: 'msgRteHeading', titleFallback: 'Heading', extra: 'font-bold', node: 'H' },
    bold: { titleKey: 'msgRteBold', titleFallback: 'Bold', extra: 'font-bold', node: 'B' },
    italic: { titleKey: 'msgRteItalic', titleFallback: 'Italic', extra: 'italic font-serif', node: 'I' },
    bullet: { titleKey: 'msgRteBulletList', titleFallback: 'Bullet list', node: <List size={14} /> },
    numbered: { titleKey: 'msgRteNumberedList', titleFallback: 'Numbered list', node: <ListOrdered size={14} /> },
    checklist: { titleKey: 'msgRteChecklist', titleFallback: 'Checklist', node: <ListChecks size={14} /> },
    link: { titleKey: 'msgRteLink', titleFallback: 'Link', node: <LinkIcon size={14} /> },
};

// The historical board set. Exported so the "boards did NOT gain the note
// editor's controls" invariant is pinned by a test instead of by memory: this
// array is what every RichTextEditor that does not pass `toolbar` receives.
export const DEFAULT_TOOLBAR = ['header', 'bold', 'bullet', 'numbered'];

const ToolbarButton = ({ kind, title, children, extra, onMouseDown, onClick }) => (
    <button
        type="button"
        data-testid={`md-${kind}`}
        aria-label={title}
        title={title}
        // preventDefault on mousedown keeps the editor's current selection intact
        // (so wrap/format applies to what the user selected, not nothing).
        onMouseDown={onMouseDown}
        onClick={onClick}
        className={`px-2 py-1 text-xs rounded hover:bg-white/[0.09] text-pai-secondary ${extra || ''}`}
    >
        {children}
    </button>
);

// ── The component ──────────────────────────────────────────────────────────

const RichTextEditor = ({
    initialMarkdown = '',
    onChange,
    onSubmit,
    placeholder = '',
    editorTestId,
    toolbarTestId,
    minHeight,
    submitOnEnter = false,
    // Ticket detail Update #280: when true the formatting toolbar is HIDDEN until
    // the user focuses the editing surface (the comment composer's contract —
    // "matching the behavior of the Description field", whose toolbar only exists
    // in edit mode). Toolbar clicks keep focus in the editor (the buttons
    // preventDefault on mousedown), so the toolbar never hides mid-format.
    collapsibleToolbar = false,
    ariaLabel,
    // Note editor Update #281: WHICH formatting controls the toolbar offers, in
    // order. The default is the historical board set; the note editor passes the
    // mockup's set (bold · italic · bullet · numbered · checklist · link), so
    // restyling one surface can never silently change the other.
    toolbar = DEFAULT_TOOLBAR,
    // tick boxes note #314: render GFM task items as interactive circles, and
    // enable the Enter-continues-the-checklist behaviour. OPT-IN — `markdownToHtml`
    // is shared, so leaving this off keeps the board editors byte-identical.
    tasks = false,
}) => {
    const { t } = useLanguage();
    const editorRef = useRef(null);
    const [focused, setFocused] = useState(false);
    const [linkOpen, setLinkOpen] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');
    // execCommand acts on the LIVE selection, which is gone by the time the user
    // has finished typing a URL into the input below — so the caret's range is
    // captured when the Link button is pressed and restored on Apply.
    const savedRangeRef = useRef(null);
    // A non-empty composer keeps the toolbar mounted through a blur — otherwise
    // clicking Send would collapse the box between mousedown (blur) and mouseup,
    // moving the button out from under the user's click.
    const [hasContent, setHasContent] = useState(
        () => !!String(initialMarkdown || '').trim(),
    );
    const showToolbar = !collapsibleToolbar || focused || hasContent;

    // Seed the editor's HTML ONCE from the stored markdown. Setting innerHTML does
    // NOT fire an input event, so this never spuriously marks the field dirty; it's
    // idempotent, so React StrictMode's double-invoke in dev is harmless.
    useEffect(() => {
        if (editorRef.current) {
            editorRef.current.innerHTML = markdownToHtml(initialMarkdown, { tasks });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const serialize = () => (editorRef.current ? htmlToMarkdown(editorRef.current) : '');

    const emit = () => {
        if (tasks && editorRef.current) normalizeTasks(editorRef.current);
        const md = serialize();
        if (collapsibleToolbar) setHasContent(!!md.trim());
        onChange?.(md);
    };

    const exec = (command, value = null) => {
        const ed = editorRef.current;
        if (!ed) return;
        ed.focus();
        try { document.execCommand('styleWithCSS', false, false); } catch { /* noop */ }
        try { document.execCommand(command, false, value); } catch { /* noop */ }
        emit();
    };

    // Heading toggles: if the caret's block is already a heading, revert to a
    // paragraph (so a second click cleanly UN-heads it — no leftover artifacts).
    const toggleHeading = () => {
        const ed = editorRef.current;
        if (!ed) return;
        ed.focus();
        const sel = window.getSelection();
        let block = sel && sel.anchorNode
            ? (sel.anchorNode.nodeType === 1 ? sel.anchorNode : sel.anchorNode.parentElement)
            : null;
        while (block && block !== ed && !/^(H[1-6]|P|DIV|LI)$/.test(block.tagName || '')) {
            block = block.parentElement;
        }
        const isHeading = block && /^H[1-6]$/.test(block.tagName || '');
        try { document.execCommand('formatBlock', false, isHeading ? 'p' : 'h2'); } catch { /* noop */ }
        emit();
    };

    // Enter inside a checklist item continues the checklist (#314). Returns true
    // when it handled the key. Two branches:
    //  • the item still has text → a NEW pending item below, carrying whatever
    //    sat to the RIGHT of the caret (so Enter at the end starts an empty item
    //    and Enter mid-line splits, exactly like a normal list);
    //  • the item is EMPTY → the checklist ENDS (the universal editor
    //    convention — otherwise there is no way out except deleting a circle).
    //    Items below the caret keep their own list after the new paragraph, so
    //    nothing is ever reordered or lost.
    const handleTaskEnter = (e) => {
        const ed = editorRef.current;
        const sel = window.getSelection();
        if (!ed || !sel || !sel.rangeCount) return false;
        const li = closestTaskLi(ed, sel.anchorNode);
        if (!li) return false;
        e.preventDefault();

        const text = taskTextOf(li);
        const list = li.parentElement;

        if (!text.textContent.trim()) {
            const para = document.createElement('p');
            para.appendChild(document.createElement('br'));
            const trailing = [];
            for (let s = li.nextElementSibling; s; s = s.nextElementSibling) trailing.push(s);
            li.remove();
            list.parentElement.insertBefore(para, list.nextSibling);
            if (trailing.length) {
                const rest = document.createElement(list.tagName.toLowerCase());
                trailing.forEach((item) => rest.appendChild(item));
                para.parentElement.insertBefore(rest, para.nextSibling);
            }
            if (!list.querySelector('li')) list.remove();
            placeCaret(para, true);
            return true;
        }

        const next = makeTaskLi(false);
        const nextText = taskTextOf(next);
        try {
            const caret = sel.getRangeAt(0);
            const tail = document.createRange();
            tail.selectNodeContents(text);
            tail.setStart(caret.endContainer, caret.endOffset);
            const moved = tail.extractContents();
            if (moved.textContent) {
                nextText.innerHTML = '';
                nextText.appendChild(moved);
            }
        } catch {
            // The caret was not inside the item's text span — start a fresh,
            // empty item rather than risk moving the wrong content.
        }
        li.parentElement.insertBefore(next, li.nextSibling);
        placeCaret(nextText, true);
        return true;
    };

    const onKeyDown = (e) => {
        if (tasks && e.key === 'Enter' && !e.shiftKey && handleTaskEnter(e)) {
            emit();
            return;
        }
        if (submitOnEnter && e.key === 'Enter' && !e.shiftKey) {
            // Enter sends the comment (#116); Shift+Enter inserts a newline / list item.
            e.preventDefault();
            onSubmit?.(serialize());
        }
    };

    // Clicking a circle flips that item. mousedown is swallowed so the click
    // never drags the caret into (or selects) the contenteditable=false node,
    // and the click never bubbles — the note's read view opens the editor on a
    // click, and toggling must not be mistaken for that.
    const onEditorMouseDown = (e) => {
        if (tasks && e.target?.closest?.('[data-task-toggle]')) e.preventDefault();
    };

    const onEditorClick = (e) => {
        if (!tasks) return;
        const toggle = e.target?.closest?.('[data-task-toggle]');
        if (!toggle) return;
        e.preventDefault();
        e.stopPropagation();
        const li = toggle.closest('li');
        if (!li) return;
        setTaskState(li, li.getAttribute(TASK_ATTR) !== 'done');
        emit();
    };

    // The toolbar's Checklist control: convert the touched list item(s), first
    // making them list items if they are not already. It TOGGLES — pressing it
    // on an all-checklist selection returns those items to plain bullets, the
    // same shape the Heading button's toggle has.
    const toggleChecklist = () => {
        const ed = editorRef.current;
        if (!ed) return;
        ed.focus();
        let items = selectedListItems(ed);
        if (!items.length) {
            try { document.execCommand('insertUnorderedList'); } catch { /* noop */ }
            items = selectedListItems(ed);
        }
        if (!items.length) { emit(); return; }
        const allTasks = items.every((li) => li.hasAttribute(TASK_ATTR));
        items.forEach((li) => (allTasks ? unmakeTask(li) : makeTask(li)));
        if (!allTasks && items.length === 1) {
            const text = taskTextOf(items[0]);
            // An empty new item holds only a <br>; the caret must go BEFORE it,
            // or the first keystroke lands on a second visual line.
            placeCaret(text, !text.textContent.trim());
        }
        emit();
    };

    // Remember where the caret is, so a link can be applied after the user has
    // left the editor to type a URL.
    const captureRange = () => {
        const sel = window.getSelection();
        const ed = editorRef.current;
        if (sel && sel.rangeCount && ed && ed.contains(sel.anchorNode)) {
            savedRangeRef.current = sel.getRangeAt(0).cloneRange();
        } else {
            savedRangeRef.current = null;
        }
    };

    const applyLink = () => {
        const url = linkUrl.trim();
        setLinkOpen(false);
        setLinkUrl('');
        if (!url) return;
        // Bare domains are a link too — without a scheme the browser would make
        // it a relative path inside the app.
        const href = /^[a-z][a-z0-9+.-]*:|^\/\//i.test(url) ? url : `https://${url}`;
        const ed = editorRef.current;
        if (!ed) return;
        ed.focus();
        const range = savedRangeRef.current;
        if (range) {
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
        }
        const sel = window.getSelection();
        const collapsed = !sel || sel.isCollapsed;
        try {
            // Nothing selected → insert the URL as its own linked text, so the
            // button is never a no-op.
            if (collapsed) document.execCommand('insertHTML', false, `<a href="${href}">${url}</a>`);
            else document.execCommand('createLink', false, href);
        } catch { /* noop */ }
        emit();
    };

    const runToolbar = (kind) => {
        if (kind === 'header') toggleHeading();
        else if (kind === 'bold') exec('bold');
        else if (kind === 'italic') exec('italic');
        else if (kind === 'bullet') exec('insertUnorderedList');
        else if (kind === 'numbered') exec('insertOrderedList');
        else if (kind === 'checklist') toggleChecklist();
        else if (kind === 'link') setLinkOpen((o) => !o);
    };

    return (
        // Ticket detail Update #280: ONE unified bordered box (per the mockup) — the
        // toolbar sits INSIDE the container, separated by a divider, and the old
        // purple `border-indigo-500` editing border is gone in favour of the neutral
        // whitish-greyish border used everywhere else in the ticket view.
        <div className="rounded-lg border border-pai bg-white/[0.03] overflow-hidden">
            {showToolbar && (
                <div
                    data-testid={toolbarTestId}
                    className="flex items-center gap-0.5 px-1 py-1 border-b border-pai-subtle"
                >
                    {toolbar.map((kind) => {
                        const spec = TOOLBAR_BUTTONS[kind];
                        if (!spec) return null;
                        return (
                            <ToolbarButton
                                key={kind}
                                kind={kind}
                                title={t(spec.titleKey, spec.titleFallback)}
                                extra={spec.extra}
                                onMouseDown={(e) => { e.preventDefault(); if (kind === 'link') captureRange(); }}
                                onClick={() => runToolbar(kind)}
                            >
                                {spec.node}
                            </ToolbarButton>
                        );
                    })}
                </div>
            )}
            {linkOpen && (
                <div className="flex items-center gap-2 px-2 py-1.5 border-b border-pai-subtle">
                    <input
                        type="url"
                        data-testid="md-link-url"
                        value={linkUrl}
                        autoFocus
                        onChange={(e) => setLinkUrl(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); applyLink(); }
                            if (e.key === 'Escape') { e.stopPropagation(); setLinkOpen(false); }
                        }}
                        placeholder="https://…"
                        className="flex-1 min-w-0 px-2 py-1 text-xs rounded border border-pai bg-white/[0.03] text-white focus:outline-none"
                    />
                    <button
                        type="button"
                        data-testid="md-link-apply"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={applyLink}
                        className="px-2 py-1 text-xs rounded border border-pai text-pai-secondary hover:bg-white/[0.07]"
                    >
                        {t('msgRteAddLink', 'Add')}
                    </button>
                </div>
            )}
            {/* The editing surface renders with the SAME `prose` stack as the read-only
                view (Message/BoardsPanel), so edit-mode is visually identical to saved. */}
            <div
                ref={editorRef}
                data-testid={editorTestId}
                role="textbox"
                aria-multiline="true"
                aria-label={ariaLabel}
                contentEditable
                suppressContentEditableWarning
                data-placeholder={placeholder}
                onInput={emit}
                onKeyDown={onKeyDown}
                onMouseDown={onEditorMouseDown}
                onClick={onEditorClick}
                onFocus={collapsibleToolbar ? () => setFocused(true) : undefined}
                onBlur={collapsibleToolbar ? () => setFocused(false) : undefined}
                style={minHeight ? { minHeight: `${minHeight}px` } : undefined}
                className="rte-editor prose prose-sm max-w-none dark:prose-invert w-full px-3 py-2 text-sm bg-transparent text-white focus:outline-none overflow-y-auto"
            />
        </div>
    );
};

export default RichTextEditor;
